import { type Player, PlayerStatus, type Role, type Room } from '@infiltre/shared';
import { AsyncStatus } from '@/shared/lib/async';
import type { AppState } from '@/shared/lib/zustand-types';

/**
 * Pure selectors over the gameStore (`room`, `privateAssignment`,
 * `eliminationHistory`, handoff). Identity-aware reads live in `hooks.ts`
 * because identity belongs to the sessionStore — combining stores happens
 * at the React layer, not in selector code.
 *
 * Each derived array has a single-slot memo cache keyed by reference; pairs
 * cleanly with `useShallow`. If multi-room consumption lands later, the
 * cache shape needs to grow into a Map.
 */

export const selectRoom = (s: AppState): Room | null =>
  s.room.status === AsyncStatus.Ready ? s.room.data : null;

export const selectHost = (s: AppState): Player | null => {
  const room = selectRoom(s);
  if (!room) return null;
  return room.players.find((p) => p.isHost) ?? null;
};

const memoOne = <In, Out>(fn: (input: In) => Out) => {
  let lastIn: In | typeof SENTINEL = SENTINEL;
  let lastOut: Out;
  return (input: In): Out => {
    if (input === lastIn) return lastOut;
    lastIn = input;
    lastOut = fn(input);
    return lastOut;
  };
};
const SENTINEL: unique symbol = Symbol('memo-sentinel');

const aliveFromPlayers = memoOne(
  (players: readonly Player[]): readonly Player[] =>
    players.filter((p) => p.status === PlayerStatus.Alive),
);

export const selectAlivePlayers = (s: AppState): readonly Player[] => {
  const room = selectRoom(s);
  if (!room) return EMPTY_PLAYERS;
  return aliveFromPlayers(room.players);
};

const EMPTY_PLAYERS: readonly Player[] = [];

export const selectAliveCount = (s: AppState): number => selectAlivePlayers(s).length;

export const selectMyAssignment = (s: AppState): { role: Role; word: string } | null => {
  const assignment = s.privateAssignment;
  if (!assignment) return null;
  if (!assignment.role) return null;
  return { role: assignment.role, word: assignment.word };
};

export const selectEliminatedThisRound = (s: AppState): Player | null => {
  const room = selectRoom(s);
  if (!room?.eliminatedThisRound) return null;
  return room.players.find((p) => p.id === room.eliminatedThisRound) ?? null;
};

export const selectRoundEliminations = (s: AppState) => {
  const room = selectRoom(s);
  if (!room) return EMPTY_HISTORY;
  return s.eliminationHistory.filter((e) => e.round === room.round);
};

const EMPTY_HISTORY: AppState['eliminationHistory'] = [];

/** Pure Undercover rule: every living player except the voter. */
export const selectValidVoteTargets = (s: AppState, voterId: string): readonly Player[] =>
  selectAlivePlayers(s).filter((p) => p.id !== voterId);

/** Pure helper: identity-aware "my player" lookup. Used by `useMyPlayer` and tests. */
export const findPlayerById = (room: Room | null, playerId: string | null): Player | null => {
  if (!room || !playerId) return null;
  return room.players.find((p) => p.id === playerId) ?? null;
};
